import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiClient } from '../../lib/api-client';
import { toast } from 'sonner';

interface AppliedUsersPageProps {
  jobs: any[];
}

export function AppliedUsersPage({ jobs }: AppliedUsersPageProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selected, setSelected] = useState<any>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [profileData, setProfileData] = useState<{ user: any; resumes: any[] } | null>(null);

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const jobId = params.get('jobId') || '';

  const job = useMemo(() => {
    return jobs.find((j: any) => String(j.id || j._id) === String(jobId));
  }, [jobs, jobId]);

  useEffect(() => {
    if (!jobId) return;
    (async () => {
      setLoading(true);
      try {
        const params: any = { limit: 500, jobId };
        if (filterStatus !== 'all') params.status = filterStatus;
        const res = await apiClient.getAdminApplications(params);
        const arr = Array.isArray((res as any)?.applications) ? (res as any).applications : (Array.isArray(res) ? (res as any) : []);
        let usersList: any[] = [];
        try {
          const usersRes: any = await apiClient.getAllUsers({ limit: 1000 });
          usersList = Array.isArray(usersRes?.users) ? usersRes.users : (Array.isArray(usersRes) ? usersRes : []);
        } catch {
          usersList = [];
        }
        const userById: Record<string, any> = {};
        for (const u of (usersList || [])) {
          const id = String((u as any)?.id || (u as any)?._id || '');
          if (id) userById[id] = u;
        }
        const mapped = arr.map((app: any) => {
          const uid = String(
            app.job_seeker_id ||
            (app.job_seeker && (app.job_seeker._id || app.job_seeker.id)) ||
            app.user_id ||
            app.userId ||
            ''
          );
          const uref = app.user || userById[uid] || null;
          return {
            id: app._id || app.id,
            appliedAt: app.applied_at || app.appliedAt,
            status: app.status || 'pending',
            userName: (uref && (uref.full_name || uref.name || uref.email)) || app.applicantName || 'Unknown',
            userEmail: (uref && (uref.email)) || app.applicantEmail || 'N/A',
            resumeUrl: app.resume_url || app.resumeUrl || (uref && (uref.profile?.resume_url || uref.profile?.resume)) || null,
            coverLetter: app.cover_letter || app.coverLetter,
            userId: uid
          };
        });
        setItems(mapped);
      } finally {
        setLoading(false);
      }
    })();
  }, [jobId, filterStatus]);

  const filtered = items.filter(app => {
    const m1 = app.userName?.toLowerCase().includes(searchTerm.toLowerCase()) || app.userEmail?.toLowerCase().includes(searchTerm.toLowerCase());
    return m1;
  });

  const refresh = async () => {
    setLoading(true);
    try {
      const params: any = { limit: 500, jobId };
      if (filterStatus !== 'all') params.status = filterStatus;
      const res = await apiClient.getAdminApplications(params);
      const arr = Array.isArray((res as any)?.applications) ? (res as any).applications : (Array.isArray(res) ? (res as any) : []);
      let usersList: any[] = [];
      try {
        const usersRes: any = await apiClient.getAllUsers({ limit: 1000 });
        usersList = Array.isArray(usersRes?.users) ? usersRes.users : (Array.isArray(usersRes) ? usersRes : []);
      } catch {
        usersList = [];
      }
      const userById: Record<string, any> = {};
      for (const u of (usersList || [])) {
        const id = String((u as any)?.id || (u as any)?._id || '');
        if (id) userById[id] = u;
      }
      const mapped = arr.map((app: any) => {
        const uid = String(
          app.job_seeker_id ||
          (app.job_seeker && (app.job_seeker._id || app.job_seeker.id)) ||
          app.user_id ||
          app.userId ||
          ''
        );
        const uref = app.user || userById[uid] || null;
        return {
          id: app._id || app.id,
          appliedAt: app.applied_at || app.appliedAt,
          status: app.status || 'pending',
          userName: (uref && (uref.full_name || uref.name || uref.email)) || app.applicantName || 'Unknown',
          userEmail: (uref && (uref.email)) || app.applicantEmail || 'N/A',
          resumeUrl: app.resume_url || app.resumeUrl || (uref && (uref.profile?.resume_url || uref.profile?.resume)) || null,
          coverLetter: app.cover_letter || app.coverLetter,
          userId: uid
        };
      });
      setItems(mapped);
      toast.success('Refreshed applications');
    } catch {
      toast.error('Failed to refresh');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserProfile = async (userId: string) => {
    try {
      const json: any = await apiClient.request(`/admin/users/${userId}/profile`);
      const resumes = Array.isArray((json as any)?.resumes) ? (json as any).resumes : [];
      setProfileData({ user: (json as any)?.user ?? json, resumes });
      setShowDetails(true);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load user profile');
    }
  };

  const downloadAdminResumePdf = async (resumeId: string) => {
    try {
      const blob = await apiClient.requestBlob(`/admin/resumes/${resumeId}/export/pdf`, { method: 'GET' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Resume.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to download PDF');
    }
  };

  const downloadAdminResumeDocx = async (resumeId: string) => {
    try {
      const blob = await apiClient.requestBlob(`/admin/resumes/${resumeId}/export/docx`, { method: 'GET' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Resume.docx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Word file downloaded');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to download Word');
    }
  };

  const exportCsv = () => {
    const rows = filtered.map(app => [app.userName, app.userEmail, job?.title || '', app.status, app.appliedAt ? new Date(app.appliedAt).toLocaleDateString() : '']);
    const csv = [
      ['Applicant Name', 'Email', 'Job Title', 'Status', 'Applied Date'].join(','),
      ...rows.map(r => r.join(','))
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `applications_${filterStatus}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} applications`);
  };

  const updateStatus = async (applicationId: string, next: string) => {
    try {
      await apiClient.updateApplicationStatus(applicationId, next as any);
      const updated = items.map(app => app.id === applicationId ? { ...app, status: next } : app);
      setItems(updated);
      toast.success('Status updated');
    } catch {
      toast.error('Failed to update status');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Applied Users ({filtered.length})</h2>
          <p className="text-sm text-gray-600">{job ? `Applicants for ${job.title}.` : 'Applicants for selected job.'}</p>
        </div>
        <div className="flex items-center gap-2">
          <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search" className="w-40" />
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-2 py-2 bg-white border rounded text-sm">
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="reviewed">Reviewed</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
          </select>
          <Button variant="outline" onClick={refresh} disabled={loading}>Refresh</Button>
          <Button variant="outline" onClick={exportCsv}>Export</Button>
          <Button
            variant="outline"
            onClick={() => {
              const search = new URLSearchParams(location.search);
              search.set('section', 'job-post-management');
              search.delete('jobId');
              navigate({ pathname: location.pathname, search: `?${search.toString()}` });
            }}
          >
            Back to Jobs
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Applicants</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-gray-600">Loading applications...</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3">Applicant</th>
                    <th className="text-left py-2 px-3">Contact</th>
                    <th className="text-left py-2 px-3">Applied</th>
                    <th className="text-left py-2 px-3">Status</th>
                    <th className="text-left py-2 px-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td className="py-3 px-3 text-gray-600" colSpan={4}>No applications found for this job.</td>
                    </tr>
                  ) : (
                    filtered.map((ap) => (
                      <tr key={ap.id} className="border-b">
                        <td className="py-2 px-3">{ap.userName}</td>
                        <td className="py-2 px-3 text-gray-700">{ap.userEmail || 'N/A'}</td>
                        <td className="py-2 px-3">{ap.appliedAt ? new Date(ap.appliedAt).toLocaleDateString() : '—'}</td>
                        <td className="py-2 px-3">
                          <Badge variant="outline" className="text-xs">{ap.status}</Badge>
                        </td>
                        <td className="py-2 px-3">
                          <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={() => { setSelected(ap); fetchUserProfile(String(ap.userId || '')); }}>View</Button>
                            {ap.resumeUrl ? (
                              <a href={ap.resumeUrl} target="_blank" rel="noreferrer" className="text-blue-600 text-xs">Resume</a>
                            ) : null}
                            <select value={ap.status} onChange={(e) => updateStatus(ap.id, e.target.value)} className="px-2 py-1 bg-white border rounded text-xs">
                              <option value="pending">Pending</option>
                              <option value="reviewed">Reviewed</option>
                              <option value="accepted">Accepted</option>
                              <option value="rejected">Rejected</option>
                            </select>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showDetails} onOpenChange={(o) => { if (!o) { setShowDetails(false); setSelected(null); setProfileData(null); } }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Applicant Profile</DialogTitle>
            <DialogDescription>Full profile and resume downloads</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <div className="text-sm font-medium">{selected.userName}</div>
                  <div className="text-sm text-gray-600">{selected.userEmail || 'N/A'}</div>
                  <div className="text-xs text-gray-500">{selected.appliedAt ? new Date(selected.appliedAt).toLocaleString() : ''}</div>
                </div>
                <div className="space-y-1">
                  {selected.coverLetter && <div className="text-sm">{selected.coverLetter}</div>}
                </div>
              </div>

              {profileData && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <div className="text-sm font-medium">Profile</div>
                      <div className="text-sm text-gray-700">
                        {(profileData.user?.full_name || profileData.user?.name) && <div>{profileData.user?.full_name || profileData.user?.name}</div>}
                        {profileData.user?.email && <div>{profileData.user.email}</div>}
                        {profileData.user?.phone && <div>{profileData.user.phone}</div>}
                        {profileData.user?.location && <div>{profileData.user.location}</div>}
                      </div>
                      {profileData.user?.profile?.summary && (
                        <div className="text-sm text-gray-700">{profileData.user.profile.summary}</div>
                      )}
                      {Array.isArray(profileData.user?.profile?.skills) && profileData.user.profile.skills.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {profileData.user.profile.skills.map((s: any, i: number) => (
                            <Badge key={i} variant="secondary">{String(s)}</Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      {Array.isArray(profileData.user?.profile?.experience) && profileData.user.profile.experience.length > 0 && (
                        <div className="space-y-2">
                          {profileData.user.profile.experience.map((exp: any, idx: number) => (
                            <div key={idx} className="border-l-4 border-blue-200 pl-3">
                              <div className="text-sm font-medium">{exp.jobTitle || exp.title}</div>
                              <div className="text-xs text-gray-600">{exp.company}</div>
                              <div className="text-xs text-gray-500">{exp.startDate} - {exp.isCurrentJob ? 'Present' : exp.endDate}</div>
                              {exp.description && <div className="text-xs text-gray-700">{exp.description}</div>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {Array.isArray(profileData.user?.profile?.education) && profileData.user.profile.education.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-sm font-medium">Education</div>
                      <div className="space-y-2">
                        {profileData.user.profile.education.map((edu: any, idx: number) => (
                          <div key={idx} className="border-l-4 border-blue-200 pl-3">
                            <div className="text-sm font-medium">{edu.degree} {edu.fieldOfStudy ? `- ${edu.fieldOfStudy}` : ''}</div>
                            <div className="text-xs text-gray-600">{edu.institution}</div>
                            <div className="text-xs text-gray-500">{edu.startDate} - {edu.isCurrentlyStudying ? 'Present' : edu.endDate}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="space-y-3">
                    <div className="text-sm font-medium">Resumes</div>
                    {profileData.resumes && profileData.resumes.length > 0 ? (
                      <div className="space-y-2">
                        {profileData.resumes.map((r: any) => (
                          <div key={String(r.id || r._id)} className="flex items-center justify-between border rounded p-2">
                            <div className="text-sm">
                              <div className="font-medium">{r.title || 'Resume'}</div>
                              <div className="text-xs text-gray-600">Template: {r.template || 'Default'}</div>
                              {r.updatedAt && <div className="text-xs text-gray-500">Updated: {new Date(r.updatedAt).toLocaleString()}</div>}
                            </div>
                            <div className="flex items-center gap-2">
                              <Button size="sm" variant="outline" onClick={() => downloadAdminResumePdf(String(r.id || r._id))}>Download PDF</Button>
                              <Button size="sm" variant="outline" onClick={() => downloadAdminResumeDocx(String(r.id || r._id))}>Download DOCX</Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-600">No resumes found. {selected?.resumeUrl ? (<a href={selected.resumeUrl} target="_blank" rel="noreferrer" className="text-blue-600">Open uploaded resume</a>) : '—'}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
